import { Component, OnInit, Inject, Output, EventEmitter, OnDestroy } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Store, Select } from '@ngxs/store';
import { Observable, Subject, BehaviorSubject, combineLatest } from 'rxjs';
import { WizardState } from '../../../modules/wizard/store/wizard.state';
import { InviteCollaborators } from '../../../modules/wizard/store/wizard.actions';
import { JobFile } from '@core/models/job-file/job-file.model';
import { User } from '@core/models/user.model';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Collaborator } from '../../../modules/wizard/models/collaborator.model';
import { DialogHandlerService } from '@core/services/dialog-handler.service';
import { InviteCollaboratorsComponent } from '../invite-collaborators/invite-collaborators.component';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { CollaboratorsInvitationModel } from 'app/modules/wizard/models/collaborators-invitation.model';
import { JobFileService } from '@core/services/job-file.service';
import { AuthState } from '@core/store/auth/auth.state';
import { CollaboratorsState } from '../../store/collaborators/collaborators.state';
import { FetchCollaboratorData } from '../../store/collaborators/collaborators.actions';
import { OrgUser } from 'app/modules/organization/models/org-user';
import { EntityMap } from '@shared/models/types';

@Component({
  selector: 'hmt-colloborater-settings',
  templateUrl: './colloborater-settings.component.html',
  standalone: false,
  styleUrls: ['./colloborater-settings.component.scss']
})
export class ColloboraterSettingsComponent implements OnInit, OnDestroy {
  @Output() inviteCollaboratorsEmitter: EventEmitter<CollaboratorsInvitationModel> = new EventEmitter();
  @Output() removeCollaboratorEmitter: EventEmitter<string> = new EventEmitter();
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  private readonly destroy$ = new Subject<void>();

  @Select(CollaboratorsState.getCollaboratorPhotos) collaboratorPhotos$!: Observable<EntityMap<string, string>>;
  @Select(CollaboratorsState.getCollaboratorData) collaboratorData$!: Observable<EntityMap<string, OrgUser>>;
  
  guestsWithPhotos$!: Observable<Array<{
    user: string;
    name: string;
    email: string;
    createdAt: Date;
    permission: 'Can View' | 'Can Edit';
    status: 'Active' | 'Suspended';
    color: string;
    avatarUrl?: string;
  }>>;

  inviteEmails: string[] = ['admin@haulmatic.com'];
  inviteInput = '';
  inviteRole: 'Guest' | 'Internal' = 'Guest';
  linkAccess: 'Anyone with the link has access' | 'Only invited people' = 'Anyone with the link has access';

  pending: Array<{
    name: string;
    email: string;
    invitedAt: Date;
    type: 'Guest' | 'Internal';
    color: string;
  }> = [];

  guests: Array<{
    user: string;
    name: string;
    email: string;
    createdAt: Date;
    permission: 'Can View' | 'Can Edit';
    status: 'Active' | 'Suspended';
    color: string;
  }> = [];

  jobFile$: Observable<JobFile | null>;
  internalCollaborators$: Observable<User[]>;
  private internalCollaboratorsSubject = new BehaviorSubject<User[]>([]);
  constructor(private store: Store,
              private dialogHandlerService: DialogHandlerService,
              private jobFileService: JobFileService,
              @Inject(MAT_DIALOG_DATA) public dialogData: { collaborators?: Collaborator[], jobRefId?: string } | null) {
    this.jobFile$ = this.store.select(WizardState.getJobFile);
    this.internalCollaborators$ = this.internalCollaboratorsSubject.asObservable();
  }

  ngOnInit(): void {
    // Initialize guestsWithPhotos$ observable
    this.updateGuestsWithPhotos();

    this.fetchInternalCollaborators();
    
    // Initialize with dialogData collaborators
    if (this.dialogData?.collaborators?.length) {
      this.updateGuestsList(this.dialogData.collaborators);
    }
    
    // Subscribe to jobFile$ for real-time updates
    this.jobFile$.pipe(takeUntil(this.destroy$)).subscribe(jobFile => {
      if (jobFile && jobFile.collaborators) {
        this.updateGuestsList(jobFile.collaborators);
      }
    });
  }
  
  private updateGuestsList(collaborators: Collaborator[]): void {
    this.guests = collaborators.map(c => ({
      user: c.user,
      name: c.name,
      email: c.email,
      createdAt: new Date(),
      permission: 'Can View',
      status: 'Active',
      color: this.generateColorFor(c.user)
    }));
    
    // Fetch collaborator data and photos
    this.fetchCollaboratorThumbnails();
    this.updateGuestsWithPhotos();
  }

  private fetchCollaboratorThumbnails(): void {
    if (!this.guests || this.guests.length === 0) {
      return;
    }

    // Extract user IDs from guests
    const collaboratorUserIds = this.guests
      .map(g => g.user)
      .filter(userId => userId != null && userId !== '');

    if (collaboratorUserIds.length > 0) {
      // Dispatch action to fetch collaborator data and photos
      this.store.dispatch(new FetchCollaboratorData(collaboratorUserIds));
    }
  }

  private updateGuestsWithPhotos(): void {
    // Combine guests with collaborator data and photos to get avatar URLs
    this.guestsWithPhotos$ = combineLatest([
      this.collaboratorPhotos$,
      this.collaboratorData$
    ]).pipe(
      map(([photos, collaboratorData]) => {
        return this.guests.map(g => {
          let avatarUrl: string | undefined;
          
          if (g.user && collaboratorData[g.user]) {
            // Get the OrgUser to find photo ID
            const orgUser = collaboratorData[g.user];
            if (orgUser.photo && photos[orgUser.photo]) {
              avatarUrl = photos[orgUser.photo];
            }
          }
          
          return {
            ...g,
            avatarUrl
          };
        });
      }),
      takeUntil(this.destroy$)
    );
  }

  // Personalize invitation
  showPersonalize = false;
  personalizeText = 'Hi there,\n\nI’d like to invite you to collaborate on this booking. You’ll be able to view the details and, depending on your role, make changes.\n\nThanks!';

  addEmail(): void {
    const value = (this.inviteInput || '').trim();
    if (value) {
      this.inviteEmails.push(value);
      this.inviteInput = '';
    }
  }

  removeEmail(index: number): void {
    if (index >= 0) {
      this.inviteEmails.splice(index, 1);
    }
  }

  togglePersonalize(): void {
    this.showPersonalize = !this.showPersonalize;
  }

  invite(): void {
    // Placeholder: integrate with InviteCollaborators flow (user-id based) or email invite service
    this.inviteEmails = [];
    this.inviteInput = '';
  }

  copyLink(): void {
    const link = 'https://example.com/invite/abc123';
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(link);
    }
  }

  private generateColorFor(seed: string): string {
    // Deterministic pastel color from a string seed (user id)
    let hash = 0;
    for (let i = 0; i < seed?.length; i++) {
      hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 70%)`;
  }

  inviteCollaborators(): void {
    const cancelSubscriptionSubject = new Subject();
    const data = {
      internalCollaborators: this.internalCollaborators$,
      existingCollaborators: this.guests // Pass existing collaborators to prevent duplicates
    };

    const dialogRef = this.dialogHandlerService.openDialog(InviteCollaboratorsComponent, data, {
      position: {}, height: '', width: '580px'
    });

    dialogRef.componentInstance.searchCollaborator
      .pipe(
        takeUntil(cancelSubscriptionSubject),
        tap((searchText: string) => {
          this.fetchInternalCollaborators(searchText);
        })
      ).subscribe();

    dialogRef.componentInstance.inviteCollaboratorsEmitter
      .pipe(
        take(1),
        tap((collaboratorsInvitationModel: CollaboratorsInvitationModel) => {
          collaboratorsInvitationModel.orgId = this.store.selectSnapshot(AuthState.getOrgId);
          collaboratorsInvitationModel.jobRefId = this.dialogData?.jobRefId;
          this.inviteCollaboratorsEmitter.emit(collaboratorsInvitationModel);

        })
      )
      .subscribe();

    dialogRef.beforeClosed()
      .pipe(
        tap(_ => {
          cancelSubscriptionSubject.next();
          cancelSubscriptionSubject.complete();
        })
      )
      .subscribe();
  }

  private fetchInternalCollaborators(searchText?: string): void {
    const scopesForCollaborators = ['create:job-file', 'approve:job-file'];
    this.jobFileService.fetchUsersByScopes(scopesForCollaborators, 0, 20, searchText)
      .pipe(
        map(res => res?.content ?? [])
      )
      .subscribe({
        next: (users: User[]) => this.internalCollaboratorsSubject.next(users),
        error: () => this.internalCollaboratorsSubject.next([])
      });
  }

  removeCollaborator(collaboratorUserId: string): void {
    this.guests = this.guests.filter(g => g.user !== collaboratorUserId);
    this.updateGuestsWithPhotos();
    this.removeCollaboratorEmitter.emit(collaboratorUserId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
